/**
 * Admin Data Seeding Page
 * 
 * This page allows you to quickly seed Firebase with sample data
 * without manually entering it in the Firebase Console.
 * 
 * This tool is intentionally kept out of the live route map.
 */

import { getAuth } from 'firebase/auth';
import { addDoc, collection, doc, getDocs, getFirestore, setDoc, Timestamp } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { app } from '../firebase';
import './AdminSeed.css';

const db = getFirestore(app);
const auth = getAuth(app);

const AdminSeed = () => {
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const user = auth.currentUser;
        if (user) {
            setCurrentUser(user.uid);
        }
    }, []);

    // Generate demo user IDs
    const demoUserIds = [
        currentUser || 'demo-user-1',
        'demo-user-2',
        'demo-user-3',
        'demo-user-4',
        'demo-user-5'
    ];

    // Showcase metadata only. Tracks are not seeded unless they include a real audio URL.
    // Use Admin Catalog or Upload for launch-ready tracks because those upload audio first.
    const sampleTracks = [
        // Nightline Audio (2)
        { title: 'Palm Drive', artist: 'Nightline Audio', userId: demoUserIds[1], genre: 'Hip-Hop', price: 20.00, bpm: 138, coverImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop', isTopSeller: false },
        { title: 'Rain Theory', artist: 'Nightline Audio', userId: demoUserIds[1], genre: 'R&B', price: 19.00, bpm: 92, coverImage: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop', isTopSeller: false },

        // Luxe Drums (2)
        { title: '808 Mirage', artist: 'Luxe Drums', userId: demoUserIds[2], genre: 'Trap', price: 26.00, bpm: 148, coverImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop', isTopSeller: false },
        { title: 'Tight Snare', artist: 'Luxe Drums', userId: demoUserIds[2], genre: 'Hip-Hop', price: 21.00, bpm: 136, coverImage: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=400&h=400&fit=crop', isTopSeller: false },

        // Paper Lantern (1)
        { title: 'Golden Room', artist: 'Paper Lantern', userId: demoUserIds[3], genre: 'Afrobeat', price: 24.00, bpm: 112, coverImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop', isTopSeller: true },

        // Lowkey Hours (1)
        { title: 'Satin', artist: 'Lowkey Hours', userId: demoUserIds[4], genre: 'R&B', price: 16.00, bpm: 88, coverImage: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop', isTopSeller: false }
    ].map(track => ({
        ...track,
        fileUrl: '',
        uploadedAt: Timestamp.fromDate(new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000)), // Random date in last 30 days
        createdAt: Timestamp.now(),
        salesCount: Math.floor(Math.random() * 50),
        totalRevenue: (Math.random() * 500).toFixed(2),
        views: Math.floor(Math.random() * 1000)
    }));

    const sampleProfiles = [
        {
            userId: demoUserIds[0],
            profile: {
                name: 'dS_JS',
                bio: 'Producer & beatmaker. Clean, modern, and hard-hitting sounds.',
                profilePicture: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200&h=200&fit=crop',
                socialLinks: {
                    twitter: 'https://twitter.com/dS_JS',
                    instagram: 'https://instagram.com/dS_JS',
                    facebook: ''
                }
            },
            tracks: [],
            followers: [demoUserIds[1], demoUserIds[2], demoUserIds[3]],
            following: [demoUserIds[1]],
            recentActivity: [],
            totalEarnings: 1820.75,
            totalSales: 62,
            pendingPayout: 1274.52
        },
        {
            userId: demoUserIds[1],
            profile: {
                name: 'Nightline Audio',
                bio: 'Late-night melodies and moody textures. Hip-Hop & R&B.',
                profilePicture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop',
                socialLinks: {
                    twitter: '',
                    instagram: 'https://instagram.com/nightlineaudio',
                    facebook: ''
                }
            },
            tracks: [],
            followers: [demoUserIds[0], demoUserIds[2]],
            following: [demoUserIds[0]],
            recentActivity: [],
            totalEarnings: 640.20,
            totalSales: 21,
            pendingPayout: 448.14
        },
        {
            userId: demoUserIds[2],
            profile: {
                name: 'Luxe Drums',
                bio: 'Hard drums, clean mixes. Trap & hip-hop specialist.',
                profilePicture: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop',
                socialLinks: {
                    twitter: 'https://twitter.com/luxedrums',
                    instagram: '',
                    facebook: ''
                }
            },
            tracks: [],
            followers: [demoUserIds[0], demoUserIds[4]],
            following: [demoUserIds[0], demoUserIds[1]],
            recentActivity: [],
            totalEarnings: 520.80,
            totalSales: 18,
            pendingPayout: 364.56
        },
        {
            userId: demoUserIds[3],
            profile: {
                name: 'Paper Lantern',
                bio: 'Afrobeat colors and warm grooves.',
                profilePicture: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=200&h=200&fit=crop',
                socialLinks: {
                    twitter: '',
                    instagram: 'https://instagram.com/paperlantern',
                    facebook: ''
                }
            },
            tracks: [],
            followers: [demoUserIds[0], demoUserIds[1]],
            following: [demoUserIds[0]],
            recentActivity: [],
            totalEarnings: 310.40,
            totalSales: 12,
            pendingPayout: 217.28
        },
        {
            userId: demoUserIds[4],
            profile: {
                name: 'Lowkey Hours',
                bio: 'Soft R&B and ambient melodies for late-night sessions.',
                profilePicture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop',
                socialLinks: {
                    twitter: '',
                    instagram: '',
                    facebook: ''
                }
            },
            tracks: [],
            followers: [demoUserIds[2]],
            following: [demoUserIds[0], demoUserIds[2]],
            recentActivity: [],
            totalEarnings: 210.10,
            totalSales: 9,
            pendingPayout: 147.07
        }
    ];

    const sampleSocialFeed = [
        {
            userId: demoUserIds[0],
            userName: 'dS_JS',
            userAvatar: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop',
            content: 'Just uploaded "Chrome Heart" — trap energy for the weekend.',
            trackId: null,
            trackTitle: 'Chrome Heart',
            trackCover: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=400&h=400&fit=crop',
            likes: 62,
            likedBy: [demoUserIds[1], demoUserIds[2], demoUserIds[3]],
            comments: [
                { userId: demoUserIds[1], userName: 'Nightline Audio', text: 'This is clean 🔥', createdAt: Timestamp.fromDate(new Date(Date.now() - 1800000)) }
            ],
            createdAt: Timestamp.fromDate(new Date(Date.now() - 3600000))
        },
        {
            userId: demoUserIds[1],
            userName: 'Nightline Audio',
            userAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
            content: 'New textures on the way. Late-night sessions only.',
            trackId: null,
            trackTitle: null,
            trackCover: null,
            likes: 18,
            likedBy: [demoUserIds[0]],
            comments: [],
            createdAt: Timestamp.fromDate(new Date(Date.now() - 7200000))
        },
        {
            userId: demoUserIds[3],
            userName: 'Paper Lantern',
            userAvatar: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=100&h=100&fit=crop',
            content: 'Afrobeat groove "Golden Room" is up now.',
            trackId: null,
            trackTitle: 'Golden Room',
            trackCover: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop',
            likes: 24,
            likedBy: [demoUserIds[0], demoUserIds[2]],
            comments: [
                { userId: demoUserIds[2], userName: 'Luxe Drums', text: 'Vibe is perfect.', createdAt: Timestamp.fromDate(new Date(Date.now() - 2400000)) }
            ],
            createdAt: Timestamp.fromDate(new Date(Date.now() - 10800000))
        }
    ];

    const addResult = (message, type = 'success') => {
        setResults(prev => [...prev, { message, type, time: new Date().toLocaleTimeString() }]);
    };

    const seedTracks = async () => {
        setLoading(true);
        addResult('Starting to seed tracks...', 'info');
        
        try {
            const playableTracks = sampleTracks.filter((track) => Boolean(track.fileUrl));
            if (playableTracks.length === 0) {
                addResult('No tracks seeded. Demo track metadata has no audio files attached.', 'info');
                addResult('Use Admin Catalog or Upload to create launch-ready tracks with real audio.', 'info');
                return;
            }

            for (const track of playableTracks) {
                await addDoc(collection(db, 'tracks'), track);
                addResult(`✓ Added track: ${track.title}`, 'success');
            }
            addResult(`✅ Successfully added ${playableTracks.length} tracks!`, 'success');
        } catch (error) {
            console.error('Error seeding tracks:', error);
            addResult(`❌ Error: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };


    const seedProfiles = async () => {
        setLoading(true);
        addResult('Starting to seed profiles...', 'info');
        
        try {
            for (const profileData of sampleProfiles) {
                await setDoc(doc(db, 'profiles', profileData.userId), profileData);
                addResult(`✓ Added profile: ${profileData.profile.name}`, 'success');
            }
            addResult(`✅ Successfully added ${sampleProfiles.length} profiles!`, 'success');
        } catch (error) {
            console.error('Error seeding profiles:', error);
            addResult(`❌ Error: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const seedSocialFeed = async () => {
        setLoading(true);
        addResult('Starting to seed social feed...', 'info');
        
        try {
            // Get track IDs first if tracks were seeded
            const tracksSnapshot = await getDocs(collection(db, 'tracks'));
            const tracksMap = new Map();
            tracksSnapshot.docs.forEach(doc => {
                const data = doc.data();
                tracksMap.set(data.title, doc.id);
            });

            for (const post of sampleSocialFeed) {
                // Link track ID if track title matches
                const postData = { ...post };
                if (post.trackTitle && tracksMap.has(post.trackTitle)) {
                    postData.trackId = tracksMap.get(post.trackTitle);
                }
                await addDoc(collection(db, 'socialFeed'), postData);
                addResult(`✓ Added post from ${post.userName}`, 'success');
            }
            addResult(`✅ Successfully added ${sampleSocialFeed.length} posts!`, 'success');
        } catch (error) {
            console.error('Error seeding social feed:', error);
            addResult(`❌ Error: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const seedPurchases = async () => {
        setLoading(true);
        addResult('Starting to seed purchases...', 'info');
        
        try {
            // Get tracks and profiles
            const tracksSnapshot = await getDocs(collection(db, 'tracks'));
            const tracks = tracksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            if (tracks.length === 0) {
                addResult('⚠️ No tracks found. Seed tracks first!', 'error');
                setLoading(false);
                return;
            }

            // Create some sample purchases
            const samplePurchases = [];
            const buyers = [demoUserIds[0], demoUserIds[1], demoUserIds[2]];
            
            // Create 10-15 random purchases
            for (let i = 0; i < 12; i++) {
                const track = tracks[Math.floor(Math.random() * tracks.length)];
                const buyerId = buyers[Math.floor(Math.random() * buyers.length)];
                const sellerId = track.userId;
                
                // Don't let users buy their own tracks
                if (buyerId === sellerId) continue;
                
                const price = parseFloat(track.price) || 0;
                const priceCents = Math.max(0, Math.round(price * 100));
                const platformCommissionCents = Math.round(priceCents * 0.05);
                const platformCommission = platformCommissionCents / 100;
                const artistEarnings = (priceCents - platformCommissionCents) / 100;
                
                const purchaseDate = Timestamp.fromDate(
                    new Date(Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000) // Random date in last 60 days
                );
                
                samplePurchases.push({
                    buyerId,
                    buyerEmail: `buyer${buyers.indexOf(buyerId) + 1}@example.com`,
                    sellerId,
                    trackId: track.id,
                    trackTitle: track.title,
                    trackArtist: track.artist,
                    trackGenre: track.genre,
                    price,
                    platformCommission,
                    artistEarnings,
                    purchaseDate,
                    status: 'completed',
                    paymentMethod: 'card'
                });
            }

            for (const purchase of samplePurchases) {
                await addDoc(collection(db, 'purchases'), purchase);
                addResult(`✓ Added purchase: ${purchase.trackTitle}`, 'success');
            }
            addResult(`✅ Successfully added ${samplePurchases.length} purchases!`, 'success');
        } catch (error) {
            console.error('Error seeding purchases:', error);
            addResult(`❌ Error: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const seedAll = async () => {
        setResults([]);
        addResult('🌱 Starting comprehensive data seeding...', 'info');
        await seedProfiles();
        await seedTracks();
        await seedSocialFeed();
        await seedPurchases();
        addResult('🎉 All data seeded successfully!', 'success');
    };

    const clearResults = () => {
        setResults([]);
    };

    return (
        <div className="admin-seed-page">
            <div className="container">
                <div className="admin-header">
                    <h1>🌱 Firebase Data Seeding</h1>
                    <p>Quickly populate your Firestore database with sample data</p>
                </div>

                <div className="seed-controls">
                    <div className="control-group">
                        <h3>Seed Individual Collections</h3>
                        <div className="button-group">
                            <button
                                onClick={seedTracks}
                                disabled={loading}
                                className="seed-button tracks-button"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 18V5l12-2v13"/>
                                    <circle cx="6" cy="18" r="3"/>
                                    <circle cx="18" cy="16" r="3"/>
                                </svg>
                                Seed Tracks ({sampleTracks.length})
                            </button>
                            <button
                                onClick={seedProfiles}
                                disabled={loading}
                                className="seed-button profiles-button"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                    <circle cx="12" cy="7" r="4"/>
                                </svg>
                                Seed Profiles ({sampleProfiles.length})
                            </button>
                            <button
                                onClick={seedSocialFeed}
                                disabled={loading}
                                className="seed-button feed-button"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                Seed Social Feed ({sampleSocialFeed.length})
                            </button>
                            <button
                                onClick={seedPurchases}
                                disabled={loading}
                                className="seed-button purchases-button"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                                    <line x1="3" y1="6" x2="21" y2="6"/>
                                    <path d="M16 10a4 4 0 0 1-8 0"/>
                                </svg>
                                Seed Purchases (~12)
                            </button>
                        </div>
                    </div>

                    <div className="control-group">
                        <h3>Seed Everything</h3>
                        <button
                            onClick={seedAll}
                            disabled={loading}
                            className="seed-button seed-all-button"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            Seed All Data
                        </button>
                    </div>

                    {results.length > 0 && (
                        <div className="results-section">
                            <div className="results-header">
                                <h3>Results</h3>
                                <button onClick={clearResults} className="clear-button">
                                    Clear
                                </button>
                            </div>
                            <div className="results-list">
                                {results.map((result, index) => (
                                    <div key={index} className={`result-item ${result.type}`}>
                                        <span className="result-time">{result.time}</span>
                                        <span className="result-message">{result.message}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {loading && (
                        <div className="loading-overlay">
                            <div className="spinner"></div>
                            <p>Seeding data...</p>
                        </div>
                    )}
                </div>

                <div className="info-section">
                    <h3>ℹ️ Important Notes</h3>
                    <ul>
                        <li>Make sure you're logged in to Firebase</li>
                        <li>Firestore security rules must allow writes</li>
                        <li>This will add new documents (won't delete existing ones)</li>
                        <li>Tracks without audio are skipped. Use Admin Catalog or Upload for real audio-backed tracks.</li>
                        <li>User IDs are set to demo values - update them if needed</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default AdminSeed;
